import { getProfilePath, requestHasRawProfileUrl } from "../auth/customerIdentity";
import { getMediaUrl } from "../utils";

const PRODUCT_ALIASES = {
  discover: "discover",
  mateout: "discover",
  mate_out: "discover",
  table: "table_booking",
  table_booking: "table_booking",
  group: "group_booking",
  group_booking: "group_booking",
  package: "party_package",
  party_package: "party_package",
  party_package_booking: "party_package",
  pw_pay: "pw_pay",
  partypay: "pw_pay",
  partywitty_pay: "pw_pay",
  wallet: "pw_pay",
  events: "events",
  event: "events",
  carnival: "carnival",
  profile: "profile",
  review: "review",
  horoscope: "horoscope",
  zodiac: "horoscope",
  janamkundali: "janam_kundali",
  janam_kundali: "janam_kundali",
  kundali: "janam_kundali",
};

const PROFILE_GAP_IDS = new Set([
  "profile_incomplete",
  "username_missing",
  "email_missing",
  "face_verification_pending",
]);

function compact(value) {
  return String(value || "").trim();
}

function lower(value) {
  return compact(value).toLowerCase();
}

function firstValue(payload, keys) {
  for (const key of keys) {
    const value = payload?.[key];
    if (value !== undefined && value !== null && compact(value) !== "") {
      return value;
    }
  }
  return "";
}

function firstId(payload, keys) {
  const value = firstValue(payload, keys);
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) return String(Math.trunc(numeric));
  const text = compact(value);
  return text || "";
}

function safeInternalPath(value) {
  const target = compact(value);
  if (!target || requestHasRawProfileUrl(target) || /\/profile\?user_id=\d+/i.test(target)) {
    return "";
  }
  if (/^https?:\/\//i.test(target)) {
    try {
      const url = new URL(target);
      const isPartywitty =
        /(^|\.)partywitty\.com$/i.test(url.hostname) ||
        ["localhost", "127.0.0.1"].includes(url.hostname);
      return isPartywitty ? `${url.pathname}${url.search}${url.hash}` : target;
    } catch {
      return "";
    }
  }
  return target.startsWith("/") ? target : "";
}

function normalizeProduct(payload) {
  const raw = lower(
    firstValue(payload, [
      "product",
      "product_key",
      "product_type",
      "module",
      "module_key",
      "source",
      "entity_type",
      "booking_type",
      "lead_type",
    ]),
  ).replace(/[\s-]+/g, "_");
  return PRODUCT_ALIASES[raw] || raw;
}

function normalizeState(payload) {
  return lower(
    firstValue(payload, [
      "state",
      "status_key",
      "event_state",
      "notification_state",
      "notification_type",
      "type",
      "action",
    ]),
  ).replace(/[\s-]+/g, "_");
}

export function isNotificationUnread(note) {
  if (note?.isProfileGap) return Number(note?.is_read || 0) === 0;
  if (note?.is_read === true) return false;
  if (note?.is_read === false) return true;
  return Number(note?.is_read || 0) === 0;
}

export function getNotificationKey(note, index = 0) {
  return compact(note?.notification_key || note?.key || note?.uuid || note?.id || note?.notification_id) || `notification_${index}`;
}

export function resolveNotificationDeepLink(note) {
  const explicit = safeInternalPath(
    firstValue(note, [
      "deep_link",
      "deeplink",
      "target_url",
      "targetUrl",
      "link",
      "url",
      "action_url",
      "click_action",
    ]),
  );
  if (explicit) return explicit;

  const profilePath = getProfilePath({
    public_id: firstValue(note, [
      "target_profile_public_id",
      "profile_public_id",
      "actor_user_public_id",
      "public_id",
      "user_public_id",
    ]),
  });
  if (profilePath) return profilePath;

  const category = lower(firstValue(note, ["notification_category", "category", "scope"]));
  const product = normalizeProduct(note);
  const state = normalizeState(note);

  if (category === "horoscope" || product === "horoscope") {
    return "/discover";
  }

  if (category === "janam_kundali" || category === "janamkundali" || product === "janam_kundali") {
    return "/profile/edit";
  }

  if (PROFILE_GAP_IDS.has(state) || product === "profile") {
    return "/profile/complete";
  }

  if (product === "discover") {
    const inviteId = firstId(note, ["invite_id", "mate_out_invite_id"]);
    if (state.includes("review")) {
      const orderId = firstId(note, ["order_id", "booking_id", "invite_id"]);
      return orderId ? `/discover-meet-review/${orderId}` : "/discover";
    }
    if (inviteId) return `/my-bookings-details/${inviteId}?booking_type=mateout`;
    return "/discover";
  }

  if (product === "table_booking") {
    const id = firstId(note, ["table_booking_order_id", "table_order_id", "booking_order_id", "booking_id", "order_id", "lead_id"]);
    if (id) return `/my-bookings-details/${id}?booking_type=table`;
    return "/my-bookings/table";
  }

  if (product === "group_booking") {
    const id = firstId(note, ["group_booking_order_id", "group_order_id", "booking_order_id", "booking_id", "order_id", "lead_id"]);
    if (id) return `/my-bookings-details/${id}?booking_type=package`;
    return "/my-bookings/package";
  }

  if (product === "party_package") {
    const id = firstId(note, ["package_booking_id", "package_order_id", "booking_id", "order_id", "lead_id", "package_id"]);
    if (id) return `/package-booking/${id}`;
    const slug = firstValue(note, ["package_slug", "slug"]);
    return slug ? `/party-packages/${encodeURIComponent(slug)}` : "/party-packages";
  }

  if (product === "pw_pay") {
    const orderId = firstId(note, ["order_id", "payment_id", "transaction_id"]);
    if (state.includes("done") && orderId) return `/payment-done/${orderId}`;
    return "/transaction";
  }

  if (product === "events") {
    const club = firstValue(note, ["club_slug", "clubdetail", "club_slug1", "club_id"]);
    const event = firstValue(note, ["event_slug", "eventname", "event_slug2"]);
    const id = firstId(note, ["event_id", "id"]);
    if (club && event && id) {
      return `/event/${encodeURIComponent(club)}/${encodeURIComponent(event)}/${id}`;
    }
    if (club && event) {
      return `/event/${encodeURIComponent(club)}/${encodeURIComponent(event)}`;
    }
    return "/events";
  }

  if (product === "carnival") {
    const clubId = firstId(note, ["club_id", "venue_id"]);
    const id = firstId(note, ["carnival_id", "pass_id", "order_id", "id"]);
    const slug = firstValue(note, ["carnival_slug", "carnivalname", "slug"]);
    if (clubId && id) return `/carnival-book/${clubId}/${id}`;
    if (slug) return `/carnival/${encodeURIComponent(slug)}`;
    return "/carnivals";
  }

  if (product === "review" || state.includes("review_pending")) {
    const orderId = firstId(note, ["order_id", "booking_id", "invite_id"]);
    const clubId = firstId(note, ["club_id", "venue_id"]);
    if (orderId) return `/discover-meet-review/${orderId}`;
    if (clubId) return `/my-bookings/review/${clubId}`;
    return "/my-bookings";
  }

  return "";
}

export function normalizeNotificationPayload(input = {}, fallback = {}) {
  const data = input?.data && typeof input.data === "object" ? input.data : {};
  const notification = input?.notification && typeof input.notification === "object" ? input.notification : {};
  const payload = { ...fallback, ...data, ...notification, ...input };
  const title = firstValue(payload, ["title", "notification_title"]) || "Notification";
  const message = firstValue(payload, ["msg", "message", "body", "notification_body"]);
  const image = firstValue(payload, ["img", "image", "icon", "requester_img", "user_img"]);
  const source = firstValue(payload, ["apiSource"]) || fallback.apiSource || firstValue(payload, ["source"]) || "PUSH";
  const isRead =
    payload.is_read === true
      ? 1
      : payload.is_read === false
        ? 0
        : Number(payload.is_read ?? payload.read ?? 0);

  return {
    ...payload,
    id: firstValue(payload, ["id", "notification_id"]) || `${source}_${Date.now()}`,
    apiSource: source,
    title,
    msg: message,
    message,
    img: image ? getMediaUrl(image) || image : "",
    notification_type: firstValue(payload, ["notification_type", "type", "state"]) || "update",
    product: normalizeProduct(payload),
    state: normalizeState(payload),
    is_read: Number.isFinite(isRead) ? isRead : 0,
    date: firstValue(payload, ["date", "created_at", "createdAt"]) || new Date().toISOString(),
    deep_link: resolveNotificationDeepLink(payload),
  };
}

export function buildProfileGapNotifications(userData) {
  if (!userData) return [];
  const username = compact(userData.username || userData.user_name);
  const email = compact(userData.email);
  const profileReady =
    userData.profile_complete === true ||
    userData.profile_completed === true ||
    userData.is_profile_complete === true ||
    Number(userData.profile_completion_percentage || userData.profile_percent || 0) >= 80;
  const faceVerified = ["true", "1", "yes", "verified"].includes(
    lower(userData.is_discover_verified || userData.selfie_verified || userData.face_verified || userData.id_verified),
  );
  const items = [];
  if (!profileReady) {
    items.push({
      id: "profile_gap_profile_incomplete",
      isProfileGap: true,
      apiSource: "PROFILE_GAP",
      title: "Complete your profile",
      msg: "Add the missing basics so invites, bookings, and reviews work smoothly.",
      notification_type: "profile_incomplete",
      product: "profile",
      state: "profile_incomplete",
      is_read: 0,
      date: new Date().toISOString(),
      deep_link: "/profile/complete",
    });
  }
  if (!username) {
    items.push({
      id: "profile_gap_username_missing",
      isProfileGap: true,
      apiSource: "PROFILE_GAP",
      title: "Choose your username",
      msg: "A username helps friends and venues identify your PartyWitty profile.",
      notification_type: "username_missing",
      product: "profile",
      state: "username_missing",
      is_read: 0,
      date: new Date().toISOString(),
      deep_link: "/profile/edit",
    });
  }
  if (!email) {
    items.push({
      id: "profile_gap_email_missing",
      isProfileGap: true,
      apiSource: "PROFILE_GAP",
      title: "Add your email",
      msg: "Add an email for payment receipts, booking updates, and account recovery.",
      notification_type: "email_missing",
      product: "profile",
      state: "email_missing",
      is_read: 0,
      date: new Date().toISOString(),
      deep_link: "/profile/edit",
    });
  }
  if (!faceVerified) {
    items.push({
      id: "profile_gap_face_verification_pending",
      isProfileGap: true,
      apiSource: "PROFILE_GAP",
      title: "Face verification pending",
      msg: "Verify once to improve trust on Discover and invite flows.",
      notification_type: "face_verification_pending",
      product: "profile",
      state: "face_verification_pending",
      is_read: 0,
      date: new Date().toISOString(),
      deep_link: "/discover-verification",
    });
  }
  return items;
}

export function normalizeNotificationList(items = [], fallback = {}) {
  return (Array.isArray(items) ? items : []).map((item) => normalizeNotificationPayload(item, fallback));
}
